import React, { useEffect, useState } from 'react';
import { ConfigProvider, DatePicker } from 'antd';
import viVN from 'antd/locale/vi_VN';
import { useSelector } from 'react-redux';
import { getListMemberGrowthMonthly, IListMemberGrowthMonthly } from '../../../../api/reportCustomer/reportCustomerApi';
import { selectorInfoUser } from '../../../../store/slices/infoUserSlice';
import { formatNumber } from '../../../../services/mixin';
import { IDataDefaulFilter } from '..';
import '../styles.scss';
import { useNavigate } from 'react-router-dom';
import { IRank } from '../../../../api/selectOptions/selectOptionsApi';
import dayjs from "dayjs";

const currentMonthYear = dayjs().format("MM/YYYY");
const MONTH_KEYS = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'] as const;
type MonthKey = typeof MONTH_KEYS[number];

interface ITierMonthData {
    growth: number;
    cumulative: number;
}

interface ITierRow {
    rankId: number;
    tier: string;
    months: ITierMonthData[];
}

interface Props {
    dataFilter?: IDataDefaulFilter;
    dataRank?: IRank[]
}



const isTotalRow = (item: IListMemberGrowthMonthly) =>
    item.rankId === 0 ||
    !item.rankName ||
    item.rankName.trim().toLowerCase() === 'tổng' ||
    item.rankName.trim().toLowerCase() === 'tổng cộng';

const convertData = (apiData: IListMemberGrowthMonthly[]): ITierRow[] =>
    apiData
        .filter(item => !isTotalRow(item))
        .map(item => ({
            rankId: item.rankId,
            tier: item.rankName,
            months: MONTH_KEYS.map(key => ({
                growth: item.months[key as MonthKey]?.growthCount ?? 0,
                cumulative: item.months[key as MonthKey]?.cumulativeCount ?? 0,
            })),
        }));

const computeTotals = (apiData: IListMemberGrowthMonthly[]): ITierMonthData[] => {
    const backendTotal = apiData.find(item => isTotalRow(item));
    if (backendTotal) {
        return MONTH_KEYS.map(key => ({
            growth: backendTotal.months[key as MonthKey]?.growthCount ?? 0,
            cumulative: backendTotal.months[key as MonthKey]?.cumulativeCount ?? 0,
        }));
    }
    const tierItems = apiData.filter(item => !isTotalRow(item));
    return MONTH_KEYS.map(key => ({
        growth: tierItems.reduce((sum, item) => sum + (item.months[key as MonthKey]?.growthCount ?? 0), 0),
        cumulative: tierItems.reduce((sum, item) => sum + (item.months[key as MonthKey]?.cumulativeCount ?? 0), 0),
    }));
};

const EMPTY_MONTH: ITierMonthData = { growth: 0, cumulative: 0 };

const now = new Date();
const currentMonthIdx = now.getMonth(); // 0-indexed
const currentYear = String(now.getFullYear());

const MembershipTierMonthlyTable: React.FC<Props> = ({ dataFilter, dataRank }) => {
    const navigate = useNavigate()



    const { unitTemp } = useSelector(selectorInfoUser);
    const [rows, setRows] = useState<ITierRow[]>([]);
    const [totalRow, setTotalRow] = useState<ITierMonthData[]>(MONTH_KEYS.map(() => EMPTY_MONTH));
    const [year, setYear] = useState<string | undefined>();

    const getTitleRank = (id: number) => {
        const titleRank = dataRank?.find(item => item.id === id)?.title
        return titleRank
    }

    const fetchData = async () => {
        const result = await getListMemberGrowthMonthly(
            unitTemp, dataFilter?.unitIds, dataFilter?.rankIds, year
        );
        if (result?.status) {
            setRows(convertData(result.data ?? []));
            setTotalRow(computeTotals(result.data ?? []));
        }
    };

    useEffect(() => {
        fetchData();
    }, [year, dataFilter?.unitIds, dataFilter?.rankIds, unitTemp]);

    return (
        <div className="pt-4">
            {/* Year picker header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0px', }}>
                <span className='text-base font-medium '>Báo cáo tăng trưởng thành viên theo tháng</span>
                <ConfigProvider locale={viVN}>
                    <DatePicker
                        picker="year"
                        placeholder="Chọn năm"
                        className="custom-input-date"
                        onChange={(date) => setYear(date?.format('YYYY') ?? undefined)}
                    />
                </ConfigProvider>
            </div>

            <div className="ct-table-scroll">
                <table className="ct-table">
                    <thead>
                        <tr className="ct-thead-row1">
                            <th className="ct-sticky-col ct-th-span" style={{ width: 160, minWidth: 160 }}>
                                Hạng thành viên
                            </th>
                            {MONTH_KEYS.map((m) => (
                                <th key={m} colSpan={2} className="ct-group-header" style={{ textAlign: 'center' }}>
                                    {m}
                                </th>
                            ))}
                        </tr>
                        <tr className="ct-thead-row2">
                            <th className="ct-sticky-col ct-th-empty" style={{ width: 160, minWidth: 160 }} />
                            {MONTH_KEYS.map((m) => (
                                <React.Fragment key={m}>
                                    <th style={{ width: 130, minWidth: 130 }}>SL KH Tăng Trưởng</th>
                                    <th style={{ width: 110, minWidth: 110 }}>Lũy kế</th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.tier} className="ct-child-row">
                                <td className="ct-sticky-col ct-td-text">{row.tier}</td>
                                {MONTH_KEYS.map((_, idx) => {
                                    const cell = row.months[idx] ?? EMPTY_MONTH;
                                    const isCurrentMonth = idx === currentMonthIdx && (year === undefined || year === currentYear);
                                    return (
                                        <React.Fragment key={idx}>
                                            <td
                                                className="ct-td-num"
                                                style={isCurrentMonth ? { cursor: 'pointer', color: '#096DD9' } : undefined}
                                                // console.log('rankId:', row.rankId)

                                                // Chỉ click với tháng năm hiện tại, đối với khi lọc năm ở quá khứ thì không cho click ở tháng nào cả
                                                onClick={isCurrentMonth ?
                                                    () =>
                                                        navigate(
                                                            `/analysis-report/member-card-list`,
                                                            {
                                                                state: {
                                                                    type: 0,
                                                                    listRank: row.rankId,
                                                                    listUnit: dataFilter?.unitIds,
                                                                    year,
                                                                    title: `Khách hàng có hạng ${getTitleRank(row.rankId)} trong tháng ${currentMonthYear}`
                                                                }
                                                            }
                                                        )
                                                    :
                                                    undefined
                                                }
                                            >
                                                {formatNumber(cell.growth)}
                                            </td>
                                            <td className="ct-td-num">{formatNumber(cell.cumulative)}</td>
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        ))}

                        <tr className="ct-total-row">
                            <td className="ct-sticky-col ct-total-name">Tổng</td>
                            {MONTH_KEYS.map((_, idx) => {

                                // const cell = row.months[idx] ?? EMPTY_MONTH;
                                const cell = totalRow[idx] ?? EMPTY_MONTH;
                                const isCurrentMonth = idx === currentMonthIdx && (year === undefined || year === currentYear);
                                return (
                                    <React.Fragment key={idx}>
                                        <td className="ct-td-num"
                                            style={isCurrentMonth ? { cursor: 'pointer', color: '#096DD9' } : undefined}
                                            // console.log('rankId:', row.rankId)

                                            // Chỉ click với tháng năm hiện tại, đối với khi lọc năm ở quá khứ thì không cho click ở tháng nào cả
                                            onClick={isCurrentMonth ?
                                                () =>
                                                    navigate(
                                                        `/analysis-report/member-card-list`,
                                                        {
                                                            state: {
                                                                type: 0,
                                                                listRank: dataFilter?.rankIds,
                                                                listUnit: dataFilter?.unitIds,
                                                                year,
                                                                title: `Tổng khách hàng trong tháng ${currentMonthYear}`
                                                            }
                                                        }
                                                    )
                                                :
                                                undefined
                                            }

                                        >{formatNumber(cell.growth)}</td>
                                        <td className="ct-td-num">{formatNumber(cell.cumulative)}</td>
                                    </React.Fragment>
                                );
                            })}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div >
    );
};

export default MembershipTierMonthlyTable;
